import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { navFor, visibleNavFor } from "./nav";
import logoUrl from "@/assets/logo.svg";
import { roleLabel, useSession } from "@/api/session";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/cn";

/**
 * Sidebar + main column. `min-w-0` on the main column is what stops a wide
 * table from forcing the whole page to scroll sideways.
 *
 * Mobile collapse is explicit: below lg the sidebar becomes an overlay drawer
 * opened from the topbar, and the topbar keeps the brand, theme toggle and
 * sign-out.
 */
export function AppShell() {
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Navigating always closes the drawer, including via browser back.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Escape closes the drawer, matching the dialog primitive's behaviour.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  if (!user) return null;
  const items = visibleNavFor(user);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-canvas">
      <TopBar
        onMenu={() => setDrawerOpen((open) => !open)}
        drawerOpen={drawerOpen}
        onSignOut={logout}
      />

      <div className="mx-auto flex w-full max-w-[1600px] flex-1 items-start">
        {/* Desktop sidebar */}
        <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-64 shrink-0 overflow-y-auto border-r border-line px-3 py-4 lg:block">
          <SideNav items={items} />
          <AccountLink />
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <>
            <button
              type="button"
              aria-label="Close navigation"
              className="fixed inset-0 top-16 z-30 bg-ink/40 lg:hidden"
              onClick={() => setDrawerOpen(false)}
            />
            <aside
              id="mobile-nav"
              className="fixed left-0 top-16 z-40 h-[calc(100dvh-4rem)] w-72 overflow-y-auto border-r border-line bg-surface px-3 py-4 shadow-pop lg:hidden"
            >
              <SideNav items={items} />
              <AccountLink />
            </aside>
          </>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 md:px-6 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TopBar({
  onMenu,
  drawerOpen,
  onSignOut,
}: {
  onMenu: () => void;
  drawerOpen: boolean;
  onSignOut: () => void;
}) {
  const user = useSession((s) => s.user);
  const theme = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-line bg-surface px-4 md:px-6">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Toggle navigation"
        aria-expanded={drawerOpen}
        aria-controls="mobile-nav"
        className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink active:scale-[0.98] lg:hidden"
      >
        <Icon name={drawerOpen ? "close" : "menu"} size={20} />
      </button>

      <div className="flex min-w-0 items-center gap-2.5">
        <img
          src={logoUrl}
          alt="Mentoria"
          className="h-10 w-auto shrink-0"
          draggable={false}
        />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight tracking-tight text-ink">
            Compass
          </p>
          <p className="truncate text-xs leading-tight text-muted">
            {user ? roleLabel(user) : ""}
          </p>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink active:scale-[0.98]"
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} size={20} />
        </button>

        <span className="mx-1 hidden h-6 w-px bg-line sm:block" />

        <div className="hidden items-center gap-2.5 sm:flex">
          <Avatar name={user?.name} size="sm" />
          <div className="min-w-0 max-w-[160px]">
            <p className="truncate text-[13px] font-medium leading-tight text-ink">
              {user?.name ?? user?.email}
            </p>
            <p className="truncate text-xs leading-tight text-muted">{user?.designation}</p>
          </div>
        </div>

        <Button variant="ghost" size="icon-lg" onClick={onSignOut} aria-label="Sign out" title="Sign out">
          <Icon name="sign-out" size={20} />
        </Button>
      </div>
    </header>
  );
}

function SideNav({ items }: { items: ReturnType<typeof navFor> }) {
  return (
    <nav aria-label="Main">
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.key}>
            <NavLink
              to={item.path}
              end={item.path.split("/").length === 2}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors",
                  "hover:bg-surface-sunken active:scale-[0.99]",
                  isActive ? "bg-accent-wash font-medium text-accent" : "text-ink-soft",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon name={item.icon} size={20} className={isActive ? "text-accent" : "text-faint"} />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.awaitingBackend && (
                    <span
                      title="No data source connected yet"
                      className="shrink-0 rounded-full bg-surface-sunken px-1.5 py-0.5 font-mono text-[10px] leading-4 text-faint"
                    >
                      soon
                    </span>
                  )}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function AccountLink() {
  return (
    <div className="mt-4 border-t border-line pt-3">
      <NavLink
        to="/account"
        className={({ isActive }) =>
          cn(
            "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors hover:bg-surface-sunken",
            isActive ? "bg-accent-wash font-medium text-accent" : "text-ink-soft",
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon name="settings" size={20} className={isActive ? "text-accent" : "text-faint"} />
            <span>Account settings</span>
          </>
        )}
      </NavLink>
    </div>
  );
}
